import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

// Push Notification Manager Hook
export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      
      // Send API URL to service worker
      navigator.serviceWorker.ready.then(registration => {
        if (registration.active) {
          registration.active.postMessage({
            type: 'SET_API_URL',
            apiUrl: API_URL
          });
        }
      });
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in your browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast.success('Notifications enabled!', {
          description: 'You will receive updates about your booking'
        });
        return true;
      } else if (result === 'denied') {
        toast.error('Notifications blocked', {
          description: 'Please enable notifications in your browser settings'
        });
        return false;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Check for existing subscription
      let pushSubscription = await registration.pushManager.getSubscription();
      
      if (!pushSubscription) {
        // Create new subscription (using a placeholder VAPID key)
        // In production, you would use your own VAPID keys
        pushSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
          )
        });
      }
      
      setSubscription(pushSubscription);
      return pushSubscription;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return null;
    }
  }, [permission, requestPermission]);

  // Show a local notification (for testing or immediate feedback)
  const showLocalNotification = useCallback(async (title, options = {}) => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body: options.body || '',
        icon: options.icon || '/favicon.png',
        badge: '/favicon.png',
        tag: options.tag || 'ygo-notification',
        data: options.data || {},
        requireInteraction: options.requireInteraction || false,
        vibrate: [200, 100, 200],
        ...options
      });
      return true;
    } catch (error) {
      console.error('Error showing notification:', error);
      return false;
    }
  }, [permission, requestPermission]);

  return {
    permission,
    subscription,
    isSupported,
    requestPermission,
    subscribeToPush,
    showLocalNotification
  };
}

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Push Notification Permission Banner Component
export function PushNotificationBanner({ onEnable, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const { permission, isSupported, requestPermission, showLocalNotification } = usePushNotifications();

  useEffect(() => {
    // Show banner if notifications are supported but not yet granted
    if (isSupported && permission === 'default') {
      // Check if user has dismissed before
      const dismissed = localStorage.getItem('push-notification-dismissed');
      if (!dismissed) {
        // Show after a short delay
        const timer = setTimeout(() => setVisible(true), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [isSupported, permission]);

  const handleEnable = async () => {
    const granted = await requestPermission();
    if (granted) {
      // Show a test notification
      await showLocalNotification('Notifications Enabled!', {
        body: 'You will now receive updates about your booking',
        tag: 'welcome-notification'
      });
      onEnable?.();
    }
    setVisible(false);
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem('push-notification-dismissed', Date.now().toString());
    onDismiss?.();
  };

  if (!visible || permission !== 'default') return null;

  return (
    <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 z-50 animate-slide-down">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-orange-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">Enable Notifications</h3>
          <p className="text-sm text-slate-600 mt-1">
            Get notified when your driver is assigned or on the way.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleEnable}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2"
              data-testid="enable-notifications-btn"
            >
              <Bell className="w-4 h-4 mr-1" />
              Enable
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="text-slate-600 text-sm px-4 py-2"
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-slate-400 hover:text-slate-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Notification Status Indicator (for header)
export function NotificationStatusIndicator() {
  const { permission, isSupported, requestPermission } = usePushNotifications();

  if (!isSupported) return null;

  const handleClick = async () => {
    if (permission === 'default') {
      await requestPermission();
    } else if (permission === 'denied') {
      toast.info('Notifications are blocked', {
        description: 'Please enable notifications in your browser settings'
      });
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`p-2 rounded-full transition-colors ${
        permission === 'granted' 
          ? 'text-green-500 hover:bg-green-50' 
          : permission === 'denied'
          ? 'text-red-400 hover:bg-red-50'
          : 'text-slate-400 hover:bg-slate-100'
      }`}
      title={
        permission === 'granted' 
          ? 'Notifications enabled' 
          : permission === 'denied'
          ? 'Notifications blocked'
          : 'Click to enable notifications'
      }
    >
      {permission === 'granted' ? (
        <Bell className="w-5 h-5" />
      ) : permission === 'denied' ? (
        <BellOff className="w-5 h-5" />
      ) : (
        <Bell className="w-5 h-5" />
      )}
    </button>
  );
}

// CSS Animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-down {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-slide-down {
    animation: slide-down 0.3s ease-out;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}

// Geofencing Hook - checks user location and triggers notifications
export function useGeofencing() {
  const geofenceIntervalRef = useRef(null);
  const { permission, showLocalNotification } = usePushNotifications();

  // Start geofence monitoring (runs periodically when app is open)
  const startGeofenceMonitoring = useCallback(() => {
    if (!navigator.geolocation || permission !== 'granted') {
      return;
    }

    const token = localStorage.getItem('client_token');
    if (!token) {
      return; // Only for logged-in users
    }

    // Check every 5 minutes when app is open
    const checkGeofence = () => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Send position to service worker for geofence check
            const registration = await navigator.serviceWorker.ready;
            if (registration.active) {
              registration.active.postMessage({
                type: 'GEOFENCE_POSITION',
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                token: token,
                apiUrl: API_URL
              });
            }
          } catch (error) {
            console.log('[Geofence] Check error:', error);
          }
        },
        (error) => {
          console.log('[Geofence] Position error:', error.message);
        },
        { enableHighAccuracy: false, timeout: 30000, maximumAge: 300000 } // 5 min cache
      );
    };

    // Initial check
    checkGeofence();

    // Set up periodic checks (every 5 minutes)
    geofenceIntervalRef.current = setInterval(checkGeofence, 5 * 60 * 1000);

    return () => {
      if (geofenceIntervalRef.current) {
        clearInterval(geofenceIntervalRef.current);
      }
    };
  }, [permission]);

  // Stop geofence monitoring
  const stopGeofenceMonitoring = useCallback(() => {
    if (geofenceIntervalRef.current) {
      clearInterval(geofenceIntervalRef.current);
      geofenceIntervalRef.current = null;
    }
  }, []);

  // Listen for service worker messages requesting geofence check
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'REQUEST_GEOFENCE_CHECK') {
        startGeofenceMonitoring();
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, [startGeofenceMonitoring]);

  // Auto-start if user is logged in and has notification permission
  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (token && permission === 'granted') {
      const cleanup = startGeofenceMonitoring();
      return cleanup;
    }
  }, [permission, startGeofenceMonitoring]);

  return {
    startGeofenceMonitoring,
    stopGeofenceMonitoring
  };
}

// Geofence Provider Component - initializes monitoring
export function GeofenceProvider({ children }) {
  useGeofencing();
  return children;
}

export default PushNotificationBanner;
